import React, { useState } from 'react'
import {
  Card,
  Table,
  Pagination,
  Button,
  Space,
  DatePicker,
  Avatar,
  Typography,
  Row,
  Col,
  Tooltip,
  Divider,
  Switch,
} from 'antd'
import {
  DownloadOutlined,
  FilterOutlined,
  UserOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import useLearnerReport from '../Hooks/Reports/useLearnerReports'
import { getLocationString } from "Common/Components/LocationHelper";


const { RangePicker } = DatePicker
const { Title, Text } = Typography

export default function LearnerReport() {
  const {
    data,
    loading,
    pageNumber,
    pageSize,
    totalCount,
    orderByLatest,
    setPageNumber,
    setPageSize,
    setFilters,
    toggleSortOrder,
    resetFilters,
    downloadLearnerReport,
  } = useLearnerReport()

  const [localDateRange, setLocalDateRange] = useState(null)

  const handleApplyFilters = () => {
    setPageNumber(1)

    if (localDateRange) {
      setFilters({
        FromDate: localDateRange[0].toDate(),
        ToDate: localDateRange[1].toDate(),
      })
    } else {
      setFilters({})
    }
  }

  const handleReset = () => {
    setLocalDateRange(null)
    resetFilters()
  }

  const columns = [
    {
      title: 'Learner',
      fixed: 'left',
      width: 220,
      render: (_, record) => (
        <Space size={8}>
          <Avatar
            size={32}
            src={record.profileImage}
            icon={<UserOutlined />}
          />
          <div>
            <div style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
              {record.firstName} {record.lastName}
            </div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              ID: {record.learnerCode}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      width: 200,
      ellipsis: { showTitle: false },
      render: (email) => (
        <Tooltip title={email}>
          <Text copyable={{ tooltips: false }}>{email}</Text>
        </Tooltip>
      ),
    },
    {
  title: 'Location',
  width: 170,
  ellipsis: { showTitle: false },
  render: (_, record) => {
    const location = getLocationString(record) || 'N/A';

    return (
      <Tooltip title={location}>
        <Text>{location}</Text>
      </Tooltip>
    );
  },
},

    {
      title: 'Joined Date',
      dataIndex: 'createdOn',
      width: 120,
      align: 'center',
      render: (date) =>
        date ? dayjs(date).format('DD MMM YYYY') : 'N/A',
    },
  ]

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              Learner Performance Report
            </Title>
            <Text type="secondary">
              Comprehensive overview of learner profiles
            </Text>
          </div>

          <Space>
            <Button onClick={() => window.history.back()}>
              Back
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={downloadLearnerReport}
              loading={loading}
            >
              Download PDF
            </Button>
          </Space>
        </div>
      </div>

      {/* Filters */}
      <Card
        title={
          <Space>
            <FilterOutlined />
            Filters
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12} lg={8}>
            <Text strong>Registered Date Range</Text>
            <RangePicker
              style={{ width: '100%', marginTop: 6 }}
              format="DD/MM/YYYY"
              value={localDateRange}
              onChange={setLocalDateRange}
            />
          </Col>

          <Col xs={24} md={12} lg={4}>
            <Text strong>Sort Order</Text>
            <br />
            <Switch
              checked={orderByLatest}
              onChange={toggleSortOrder}
              checkedChildren="Latest"
              unCheckedChildren="Oldest"
              style={{ marginTop: 6 }}
            />
          </Col>
        </Row>

        <Divider />

        <Row justify="end">
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              disabled={loading}
            >
              Reset All
            </Button>
            <Button
              type="primary"
              onClick={handleApplyFilters}
              loading={loading}
            >
              Apply Filters
            </Button>
          </Space>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={false}
          scroll={{ x: 900 }}
          size="middle"
        />

        <Divider />

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Text type="secondary">
            Page {pageNumber} • {data.length} of {totalCount} learners
          </Text>
          <Pagination
            current={pageNumber}
            pageSize={pageSize}
            total={totalCount}
            showSizeChanger
            showQuickJumper
            onChange={(p, s) => {
              setPageNumber(p)
              setPageSize(s)
            }}
          />
        </div>
      </Card>
    </div>
  )
}
